import { createSelector } from 'reselect';
import type { RootState } from '../index';
import type { GetContentV1Output } from '../../lib/api/content-api-client';

/**
 * Content slice selectors (task 5.3, public-website spec).
 *
 * Pure projections of `state.content` per design §3.1 (Selectors
 * layer). Components read content payloads exclusively through these
 * selectors so the per-date cache shape in `content-slice.ts` stays an
 * implementation detail of the slice.
 *
 * Function-length budget: every export below stays under 20 lines per
 * Requirement 3.7.
 */

/**
 * Get the content payload for a specific date, or undefined if not cached.
 *
 * Pure projection of `state.content.byDate`. Not memoized via reselect
 * because the consumer passes `date` per-call — a single memoized
 * function would defeat the cache. Components consuming this selector
 * should accept the slight redundancy of a per-call lookup.
 */
export function selectContentByDate(
  state: RootState,
  date: string,
): GetContentV1Output | undefined {
  return state.content.byDate[date];
}

/**
 * Get today's content payload (UTC date) — convenience for <DailyPage>.
 *
 * Memoized via reselect: today's date string changes only at the
 * UTC midnight boundary, so the memo cache shouldn't churn within a
 * single render cycle.
 */
export const selectContentForToday = createSelector(
  [(state: RootState) => state.content.byDate],
  (byDate): GetContentV1Output | undefined => {
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD UTC
    return byDate[today];
  },
);

/**
 * Get the per-date fetch status (idle / loading / succeeded / failed).
 */
export function selectContentStatusByDate(
  state: RootState,
  date: string,
): 'idle' | 'loading' | 'succeeded' | 'failed' {
  return state.content.statusByDate[date] ?? 'idle';
}

/**
 * Get the per-date error message, if any.
 */
export function selectContentErrorByDate(
  state: RootState,
  date: string,
): string | undefined {
  return state.content.errorByDate[date];
}
